// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { MyAccountQuickLinksStyles } from "@mcmc/App/Widgets/MyAccount/MyAccountQuickLinks";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const myAccountQuickLinksFields = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "MyAccount/MyAccountQuickLinks";
    })?.fields;

    return { myAccountQuickLinksFields };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export const myAccountQuickLinksStyles: MyAccountQuickLinksStyles = {
    linkListWrapper: {
        css: css`
            margin-bottom: 25px;
            &:first-child ${StyledWrapper} {
                border-top: none;
                padding-top: 0;
            }
        `,
    },
    titleWrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.border")};
            border-bottom: 1px solid ${getColor("common.border")};
            margin-left: -20px;
            margin-right: -20px;
            margin-bottom: 15px;
            padding: 10px 20px;
        `,
    },
};

interface MyAccountViewStyles {
    container: InjectableCss;
    leftSection: InjectableCss;
    rightSection: InjectableCss;
}

export const myAccountViewStyles: MyAccountViewStyles = {
    container: {
        css: css`
            display: flex;
            margin: -15px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-wrap: wrap;
                            & > ${StyledWrapper} {
                                flex: 1 1 100%;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    leftSection: {
        css: css`
            flex: 0 0 320px;
            padding: 15px;
            min-width: 0;
        `,
    },
    rightSection: {
        css: css`
            flex: 1 1 100%;
            padding: 15px;
            min-width: 0;
        `,
    },
};

const MyAccountLayout: FC<Props> = ({ id, myAccountQuickLinksFields }) => {
    return (
        <StyledWrapper {...myAccountViewStyles.container}>
            {myAccountQuickLinksFields && (
                <StyledWrapper {...myAccountViewStyles.leftSection}>
                    <StyledBox
                        css={css`
                            padding: 15px 20px;
                        `}
                    >
                        {createWidgetElement("MyAccount/MyAccountQuickLinks", {
                            fields: { ...myAccountQuickLinksFields, extendedStyles: myAccountQuickLinksStyles },
                        })}
                    </StyledBox>
                </StyledWrapper>
            )}
            <StyledWrapper {...myAccountViewStyles.rightSection}>
                <Zone contentId={id} zoneName="content" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const myAccountLayout: WidgetModule = {
    component: connect(mapStateToProps)(MyAccountLayout),
    definition: {
        group: "My Account",
    },
};

export default myAccountLayout;
